import React, { useEffect, useMemo, useState } from 'react';
import api, { visiteAPI, prestazioniAPI } from '../../services/api';
import { templateAPI } from '../../services/templateApi';
import SelettorePrestazione from './SelettorePrestazione';
import ModuloAttestazione from './ModuloAttestazione';
import FirmaConsenso from './FirmaConsenso';
import SceltaFirma from './SceltaFirma';
import { useSporco, useUscitaProtetta } from '../../utils/uscitaProtetta';
import { notificaErrore, messaggioDa } from '../../utils/notifiche';
import { etichettaPacchetto } from '../../utils/etichettaPacchetto';

/**
 * «Nuova procedura»: la scelta semplice della prestazione, dentro la visita.
 *
 * FASE 2 DEL RIORDINO (23 settembre 2026, decisioni di Alex). Il vecchio
 * modulo di «Nuova procedura» stava in testa alla pagina, con la data da
 * scegliere, il medico da scegliere e le righe da comporre. Adesso:
 *  - la DATA e' oggi, ora italiana, e non si cambia: niente di
 *    retroattivo. La scrive il server (oggi_clinica()), qui si mostra;
 *  - l'ESECUTORE e' il medico collegato, e non si sceglie. Chi non e'
 *    collegato a un medico questa finestra non la vede, e il server lo
 *    rifiuterebbe comunque;
 *  - si sceglie UNA prestazione per volta, con l'esenzione IVA
 *    (attestazione ICD) dove la voce la ammette. Le altre prestazioni
 *    della stessa visita si aggiungono dal pulsante sotto il riquadro
 *    «Visita», ciascuna col suo riquadro.
 *
 * SOLO QUI SI SCEGLIE UNA PRESTAZIONE (Alex, 23 settembre 2026, dopo la
 * prova): «Nuova visita» crea la visita vuota di oggi, la si compila dal
 * riquadro «Visita», e le prestazioni - la visita stessa compresa, per
 * esempio «Visita dermatologica» - si aggiungono da qui, una per volta.
 * Fino a quel giorno questa finestra si apriva anche da «Nuova visita» e
 * creava la visita insieme alla prima prestazione.
 *
 * IL CONSENSO, PRIMA: una prestazione legata a un consenso obbligatorio
 * non si registra senza la firma (migration 201). Qui si chiede al server,
 * con le stesse funzioni, cosa manca, e si fa firmare PRIMA di salvare. I
 * facoltativi si propongono dopo, come prima.
 *
 * «CONTINUA PACCHETTO» E' QUESTA STESSA FINESTRA (Alex, 28/09/2026): con
 * `pacchetto` la prestazione la decide il pacchetto, la quantita' e' una, e
 * il server consuma la seduta nella stessa transazione che registra la
 * procedura. Consensi, abilitazione e riservatezza sono quelli di sempre:
 * nessuna strada laterale. Senza `pacchetto`, se la voce scelta ha un
 * pacchetto aperto per questo paziente, un avviso propone di usarlo: non
 * blocca, perche' a volte la prestazione si paga a parte davvero.
 */

const dataItaliana = (iso) => {
    const [a, m, g] = String(iso || '').slice(0, 10).split('-');
    return a && m && g ? `${g}/${m}/${a}` : '';
};

const euro = (v) => {
    const n = parseFloat(v);
    return Number.isNaN(n) ? '—' : n.toLocaleString('it-IT', { style: 'currency', currency: 'EUR' });
};

const SceltaProcedura = ({ pazienteId, visita, oggi, utente, nomeEsecutore, onCreata, onChiudi,
                           pacchetto = null, pacchettiAperti = [], onUsaPacchetto = null }) => {
    const [catalogo, setCatalogo] = useState([]);
    const [attestazioni, setAttestazioni] = useState([]);
    const [riga, setRiga] = useState(null);             // la prestazione scelta
    const [quantita, setQuantita] = useState(1);
    const [attestazioneId, setAttestazioneId] = useState('');
    const [compilaAttestazione, setCompilaAttestazione] = useState(false);
    const [verifica, setVerifica] = useState(null);     // cosa chiede il blocco sul consenso
    const [facoltativi, setFacoltativi] = useState([]);
    const [firmaFacoltativi, setFirmaFacoltativi] = useState(true);
    const [firmaPrima, setFirmaPrima] = useState(null); // { consensi }
    const [doveFirma, setDoveFirma] = useState(null);   // null | 'pc'
    const [dopo, setDopo] = useState(null);             // { visita, consensi } facoltativi da firmare dopo
    const [doveDopo, setDoveDopo] = useState(null);
    const [inCorso, setInCorso] = useState(false);

    const medicoId = utente?.medico_id;
    const titolo = pacchetto ? 'Continua pacchetto' : 'Nuova procedura';

    /* Uscire con una prestazione scelta chiede conferma, come ogni modulo. */
    const [versione] = useState(0);
    const sporco = useSporco({ riga: riga?.id || null, quantita, attestazioneId }, versione);
    const chiedi = useUscitaProtetta(sporco && !dopo);
    const chiudi = () => { if (chiedi()) onChiudi(); };

    useEffect(() => {
        prestazioniAPI.getAll(true)
            .then((r) => setCatalogo(r.data?.data?.prestazioni || []))
            .catch(() => setCatalogo([]));
        api.get(`/attestazioni/valide/${pazienteId}`)
            .then((r) => setAttestazioni(r.data?.data?.attestazioni || []))
            .catch(() => setAttestazioni([]));
    }, [pazienteId]);

    /* Solo servizi (i prodotti si vendono in cassa), e solo le voci
       generiche o quelle a cui questo medico e' abilitato (migration 202). */
    const voci = useMemo(() => catalogo.filter((p) => (!p.tipologia || p.tipologia === 'servizio')
        && (!(p.medici || []).length || p.medici.some((m) => String(m.id) === String(medicoId)))),
    [catalogo, medicoId]);

    /* Con un pacchetto la prestazione e' la sua, e non si sceglie. */
    useEffect(() => {
        if (!pacchetto) return;
        const p = catalogo.find((x) => String(x.id) === String(pacchetto.prestazione_id)) || null;
        setRiga(p);
        setQuantita(1);
        setAttestazioneId('');
    }, [pacchetto, catalogo]);
    const pacchettiDellaVoce = !pacchetto && riga
        ? pacchettiAperti.filter((p) => String(p.prestazione_id) === String(riga.id)) : [];

    const scegli = (id) => {
        const p = catalogo.find((x) => String(x.id) === String(id)) || null;
        setRiga(p);
        setAttestazioneId('');
    };

    /* Il blocco sul consenso, chiesto al server per QUESTA prestazione,
       questo paziente e chi esegue. */
    useEffect(() => {
        let vivo = true;
        setVerifica(null);
        setFacoltativi([]);
        if (!riga) return undefined;
        templateAPI.verificaConsensi({ paziente_id: pazienteId, medico_id: medicoId, prestazioni: String(riga.id) })
            .then((r) => { if (vivo) setVerifica(r.data?.data || null); })
            .catch((e) => { if (vivo) setVerifica({ errore: messaggioDa(e, 'Non riesco a verificare i consensi della prestazione') }); });
        templateAPI.getPerPrestazione(riga.id)
            .then((r) => {
                if (!vivo) return;
                setFacoltativi((r.data?.data?.template || []).filter((t) => !t.obbligatorio)
                    .map((t) => ({ ...t, _prestazione: riga.nome })));
            })
            .catch(() => { if (vivo) setFacoltativi([]); });
        return () => { vivo = false; };
    }, [riga, pazienteId, medicoId]);

    const bloccanti = (verifica?.prestazioni || []).filter((p) => p.senza_modello);
    const daFirmare = (verifica?.modelli || []).map((t) => ({ ...t, _prestazione: riga?.nome || null }));
    const giaValidi = (verifica?.prestazioni || []).flatMap((p) => p.validi || []);

    /* LA QUANTITA' SI LEGGE IN UN POSTO SOLO (03b-4, all'unione col ramo
       fase2). Qui c'era `parseFloat(...) || 1`: chi scriveva 0 vedeva 0 e
       registrava 1 - una prestazione fatturata a prezzo pieno e una distinta
       scaricata. Uno zero e' uno zero: si dice, e non si salva. */
    const quantitaNumero = () => parseFloat(String(quantita).replace(',', '.'));

    const salva = async (documenti = []) => {
        setInCorso(true);
        try {
            const prestazione = {
                prestazione_id: riga.id,
                quantita: quantitaNumero(),
                attestazione_id: attestazioneId && attestazioneId !== 'nuova' ? attestazioneId : null,
            };
            const consensi = (documenti || []).map((d) => d?.id ?? d?.documento_id).filter(Boolean);
            const r = await visiteAPI.aggiungiPrestazione(visita.id, pacchetto
                ? { prestazione: { prestazione_id: riga.id, quantita: 1 }, pacchetto_id: pacchetto.id, consensi_documenti: consensi }
                : { prestazione, consensi_documenti: consensi });
            const nata = r.data?.data?.visita;
            if (firmaFacoltativi && facoltativi.length && nata) {
                setDopo({ visita: nata, consensi: facoltativi });
                setDoveDopo(null);
                return;
            }
            onCreata(nata);
        } catch (e) {
            notificaErrore(messaggioDa(e, 'Non riesco ad aggiungere la procedura'));
        } finally {
            setInCorso(false);
        }
    };

    /* L'ordine: prima l'attestazione (se nuova), poi le firme obbligatorie,
       poi il salvataggio. Il server rifiuterebbe comunque (201): qui si
       evita di arrivarci. */
    const procedi = () => {
        if (!riga) { notificaErrore('Scegli la prestazione.'); return; }
        if (!(quantitaNumero() > 0)) { notificaErrore('La quantità deve essere maggiore di zero.'); return; }
        if (attestazioneId === 'nuova') { setCompilaAttestazione(true); return; }
        if (verifica?.errore) { notificaErrore(verifica.errore); return; }
        if (bloccanti.length) { notificaErrore(bloccanti[0].messaggio); return; }
        if (daFirmare.length) { setDoveFirma(null); setFirmaPrima({ consensi: daFirmare }); return; }
        salva();
    };

    const firmati = (documenti) => {
        setFirmaPrima(null);
        setDoveFirma(null);
        salva(documenti);
    };

    if (dopo) {
        /* I facoltativi, a procedura registrata: si firmano ora o dopo. */
        const fine = () => onCreata(dopo.visita);
        return doveDopo === 'pc' ? (
            <FirmaConsenso pazienteId={pazienteId} visitaId={dopo.visita.id} medicoId={medicoId}
                           consensi={dopo.consensi} onClose={fine} onComplete={fine} />
        ) : (
            <SceltaFirma pazienteId={pazienteId} visitaId={dopo.visita.id} medicoId={medicoId}
                         consensi={dopo.consensi} onSuPc={() => setDoveDopo('pc')}
                         onCompletato={fine} onAnnulla={fine} />
        );
    }

    return (
        <>
            <div className="modal-overlay" onClick={chiudi}>
                <div className="modal-content modal-procedura" onClick={(e) => e.stopPropagation()}>
                    <div className="modal-header">
                        <h2>{titolo}</h2>
                        <button className="btn-close" onClick={chiudi}>✕</button>
                    </div>

                    <div className="form-group">
                        <div className="esecutore-imposto">
                            Oggi, {dataItaliana(oggi)} · Esecutore: {nomeEsecutore || 'il tuo profilo medico'}
                        </div>
                    </div>

                    {pacchetto ? (
                        <div className="form-group">
                            <label>Pacchetto</label>
                            <div className="esecutore-imposto">{etichettaPacchetto(pacchetto)}</div>
                            <small className="consenso-nota">
                                {riga ? riga.nome : 'La prestazione del pacchetto non è fra quelle che puoi eseguire.'}
                                {' '}— la seduta si scala dal pacchetto: niente in cassa, il compenso sul valore della seduta.
                            </small>
                        </div>
                    ) : (
                        <div className="form-group">
                            <label>Prestazione</label>
                            <SelettorePrestazione voci={voci} valore={riga?.id || ''} onChange={scegli} />
                        </div>
                    )}

                    {pacchettiDellaVoce.length > 0 && (
                        <div className="form-group consenso-box">
                            <strong>Il paziente ha un pacchetto aperto per questa prestazione.</strong>
                            <small className="consenso-nota">
                                Registrata da qui, la prestazione va in cassa a prezzo di listino e il pacchetto resta com'è:
                                il paziente pagherebbe due volte. Se è una seduta del pacchetto, usa il pacchetto.
                            </small>
                            {onUsaPacchetto && pacchettiDellaVoce.map((p) => (
                                <button key={p.id} type="button" className="btn-secondary" onClick={() => onUsaPacchetto(p)}>
                                    Usa il pacchetto: {etichettaPacchetto(p)}
                                </button>
                            ))}
                        </div>
                    )}

                    {riga && !pacchetto && (
                        <div className="form-group prestazione-scelta">
                            <label>
                                Quantità
                                <input type="number" min="0.01" step="0.01" value={quantita}
                                       onChange={(e) => setQuantita(e.target.value)} className="prest-qta-input" />
                            </label>
                            <span className="prest-listino">Prezzo di listino: {euro(riga.prezzo_indicativo)}</span>
                        </div>
                    )}

                    {riga && !pacchetto && riga.ammette_esenzione && (
                        <div className="form-group prest-esenzione">
                            <label>Esenzione IVA</label>
                            <select value={attestazioneId} onChange={(e) => setAttestazioneId(e.target.value)}>
                                <option value="">Nessuna — prestazione imponibile</option>
                                {attestazioni.map((a) => (
                                    <option key={a.id} value={a.id}>
                                        {a.icd10_codice ? `${a.icd10_codice} — ${a.icd10_titolo}` : a.trattamento}
                                        {a.valida_al ? ` (fino al ${new Date(a.valida_al).toLocaleDateString('it-IT')})` : ''}
                                    </option>
                                ))}
                                <option value="nuova">+ Nuova attestazione…</option>
                            </select>
                            {attestazioneId === 'nuova' && (
                                <span className="prest-esenzione-nota">
                                    Prima di registrare la procedura l'attestazione si compila e si firma,
                                    altrimenti la prestazione resta imponibile.
                                </span>
                            )}
                        </div>
                    )}

                    {verifica?.errore && (
                        <div className="form-group consenso-box consenso-box--blocco"><strong>{verifica.errore}</strong></div>
                    )}
                    {bloccanti.map((p) => (
                        <div key={`blocco-${p.prestazione_id}`} className="form-group consenso-box consenso-box--blocco">
                            <strong>{p.messaggio}</strong>
                        </div>
                    ))}
                    {!bloccanti.length && daFirmare.length > 0 && (
                        <div className="form-group consenso-box">
                            <strong className="consenso-titolo">
                                Consenso obbligatorio: il paziente firma prima che la procedura venga registrata
                            </strong>
                            <ul className="consenso-elenco">
                                {daFirmare.map((t) => <li key={t.id}>{t.titolo}</li>)}
                            </ul>
                            <small className="consenso-nota">Senza la firma la prestazione non si registra.</small>
                        </div>
                    )}
                    {giaValidi.length > 0 && (
                        <div className="form-group consenso-box consenso-box--valido">
                            {giaValidi.map((c) => (
                                <small key={c.template_id} className="consenso-nota">
                                    «{c.titolo}» firmato dal paziente il{' '}
                                    {new Date(c.firmato_il).toLocaleDateString('it-IT')}: vale anche per questa procedura.
                                </small>
                            ))}
                        </div>
                    )}
                    {facoltativi.length > 0 && (
                        <div className="form-group consenso-box">
                            <label className="consenso-check">
                                <input type="checkbox" checked={firmaFacoltativi}
                                       onChange={(e) => setFirmaFacoltativi(e.target.checked)} />
                                <span>
                                    <strong>Compilazione e firma consenso</strong>
                                    <small>
                                        Registrata la procedura {facoltativi.length === 1
                                            ? 'verrà proposto il consenso'
                                            : `verranno proposti ${facoltativi.length} consensi`} da far firmare al paziente.
                                    </small>
                                </span>
                            </label>
                            <ul className="consenso-elenco">
                                {facoltativi.map((t) => <li key={t.id}>{t.titolo}</li>)}
                            </ul>
                        </div>
                    )}

                    <div className="modal-footer">
                        <button type="button" onClick={chiudi} className="btn-secondary">Annulla</button>
                        <button type="button" className="btn-primary" onClick={procedi}
                                disabled={!riga || inCorso || bloccanti.length > 0}>
                            {inCorso ? 'Registro…'
                                : daFirmare.length && !bloccanti.length ? 'Fai firmare e registra'
                                    : pacchetto ? 'Registra la seduta' : 'Aggiungi la procedura'}
                        </button>
                    </div>
                </div>
            </div>

            {compilaAttestazione && (
                <ModuloAttestazione
                    pazienteId={pazienteId}
                    trattamentoIniziale={riga?.nome}
                    onCompletata={(id) => {
                        setCompilaAttestazione(false);
                        setAttestazioneId(String(id));
                        api.get(`/attestazioni/valide/${pazienteId}`)
                            .then((r) => setAttestazioni(r.data?.data?.attestazioni || []))
                            .catch(() => {});
                    }}
                    onAnnulla={() => { setCompilaAttestazione(false); setAttestazioneId(''); }}
                />
            )}

            {firmaPrima && doveFirma === null && (
                <SceltaFirma pazienteId={pazienteId} visitaId={visita?.id || null} medicoId={medicoId}
                             consensi={firmaPrima.consensi} onSuPc={() => setDoveFirma('pc')}
                             onCompletato={firmati}
                             onAnnulla={() => { setFirmaPrima(null); setDoveFirma(null); }} />
            )}
            {firmaPrima && doveFirma === 'pc' && (
                <FirmaConsenso pazienteId={pazienteId} visitaId={visita?.id || null} medicoId={medicoId}
                               consensi={firmaPrima.consensi} obbligatori
                               onClose={() => { setFirmaPrima(null); setDoveFirma(null); }}
                               onComplete={firmati} />
            )}
        </>
    );
};

export default SceltaProcedura;
